/**
 * Photo storage: keys, URLs and thumbnail generation.
 *
 * Shared by the public site and the realtor panel so the key layout is defined
 * exactly once. The bucket holds two parallel trees:
 *
 *     <objectId>/<filename>            the original, as uploaded
 *     thumbs/<objectId>/<filename>     a ~640px JPEG for grids and cards
 *
 * Listing the album prefix therefore never returns thumbnails, and a thumbnail
 * key is always just `thumbs/` + the original key — one rule, mirrored in
 * scripts/generate_thumbnails.py.
 */

export const REGION = "eu-central-1";
export const S3_PHOTOS_BUCKET = "realtor-photos";

/**
 * Where photos are served from. Empty means "straight from S3".
 *
 * Put CloudFront in front of the bucket and set this to the distribution
 * domain — "https://d1234abcd.cloudfront.net" or your own "https://img.leocity.estate".
 * Nothing else in the app has to change. See CLOUDFRONT_SETUP.md.
 */
export const MEDIA_BASE_URL = "https://img.leocity.estate/";

export const THUMBS_PREFIX = "thumbs/";

/** Long edge of a generated thumbnail, in pixels. Cards render ~400px wide. */
export const THUMB_MAX_SIDE = 640;
export const THUMB_QUALITY = 0.72;

/** Originals below this size are already small enough to skip. */
const THUMB_SKIP_BYTES = 120 * 1024;

export const PHOTO_CACHE_CONTROL = "public, max-age=31536000, immutable";

function baseUrl() {
  if (MEDIA_BASE_URL) return MEDIA_BASE_URL.replace(/\/+$/, "") + "/";
  return "https://s3." + REGION + ".amazonaws.com/" + S3_PHOTOS_BUCKET + "/";
}

/**
 * Encode a key for use in a URL path.
 *
 * Each segment is encoded separately so the slashes stay real slashes. The old
 * code ran encodeURIComponent over the whole key, turning "/" into "%2F"; S3's
 * path-style endpoint decodes that back, but CloudFront does not reliably, so
 * per-segment encoding is what makes the CDN switch a one-line change.
 */
export function encodeKey(key) {
  return String(key)
    .split("/")
    .map(encodeURIComponent)
    .join("/");
}

export function photoUrl(key) {
  return baseUrl() + encodeKey(key);
}

export function thumbKey(key) {
  return THUMBS_PREFIX + key;
}

export function thumbUrl(key) {
  return photoUrl(thumbKey(key));
}

/** Album prefixes for a listing: [originals, thumbnails]. */
export function albumPrefixes(objectId) {
  const album = encodeURIComponent(objectId) + "/";
  return [album, THUMBS_PREFIX + album];
}

/**
 * Turn one picked File into a small JPEG Blob, in the browser.
 *
 * Best-effort by design: any failure resolves to null and the caller uploads
 * the original alone. A missing thumbnail costs bandwidth; a failed upload
 * costs the realtor their work.
 */
export function makeThumbnail(file, maxSide, quality) {
  const limit = maxSide || THUMB_MAX_SIDE;
  const q = quality || THUMB_QUALITY;

  return new Promise(function (resolve) {
    if (!file || typeof document === "undefined") return resolve(null);
    if (!/^image\//.test(file.type || "")) return resolve(null);

    var objectUrl;
    try {
      objectUrl = URL.createObjectURL(file);
    } catch (e) {
      return resolve(null);
    }

    var settled = false;
    var finish = function (result) {
      if (settled) return;
      settled = true;
      try {
        URL.revokeObjectURL(objectUrl);
      } catch (e) {
        /* nothing to clean up */
      }
      resolve(result);
    };

    // A pathological file must not hang the upload.
    var timer = setTimeout(function () {
      finish(null);
    }, 15000);

    var img = new Image();

    img.onload = function () {
      clearTimeout(timer);
      try {
        var w = img.naturalWidth;
        var h = img.naturalHeight;
        if (!w || !h) return finish(null);

        var scale = Math.min(1, limit / Math.max(w, h));
        // Already small: a second copy would save nothing.
        if (scale >= 1 && file.size <= THUMB_SKIP_BYTES) return finish(null);

        var canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(w * scale));
        canvas.height = Math.max(1, Math.round(h * scale));

        var ctx = canvas.getContext("2d");
        if (!ctx || !canvas.toBlob) return finish(null);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          function (blob) {
            // Only worth uploading if it actually beats the original.
            if (blob && blob.size < file.size) finish(blob);
            else finish(null);
          },
          "image/jpeg",
          q
        );
      } catch (e) {
        finish(null);
      }
    };

    img.onerror = function () {
      clearTimeout(timer);
      finish(null);
    };

    img.src = objectUrl;
  });
}
